import {
  Card,
  CardRank,
  CardSuit,
  Hand,
  HandRankType,
  Player,
  GameState,
  GameRound,
  SidePot,
  HandResult,
  RANK_VALUES,
  HAND_RANK_VALUES,
} from '../types/GameState';
//  SECURITY: Import bot detection service for action timing analysis

import { botDetectionService } from './security';

// ============================================================================
// GAME ENGINE CLASS
// ============================================================================

class GameEngine {
  // Card ranks and suits for deck generation
  private readonly RANKS: CardRank[] = ['2', '3', '4', '5', '6', '7', '8', '9', '10', 'J', 'Q', 'K', 'A'];
  private readonly SUITS: CardSuit[] = ['hearts', 'diamonds', 'clubs', 'spades'];

  // ============================================================================
  // DECK OPERATIONS
  // ============================================================================

  /**
   * Generate a fresh 52-card deck and shuffle it
   */
  generateDeck(): Card[] {
    const deck: Card[] = [];
    for (const suit of this.SUITS) {
      for (const rank of this.RANKS) {
        deck.push({ rank, suit });
      }
    }
    return this.shuffleDeck(deck);
  }

  /**
   *  COMPLIANCE FIX: Cryptographically Secure Fisher-Yates Shuffle

   * 
   * Uses crypto.getRandomValues() (CSPRNG) instead of Math.random() (PRNG)
   * This is REQUIRED for sweepstakes/real-money games because:
   * 
   * 1. Math.random() is predictable - uses a seeded PRNG that can be reverse-engineered
   * 2. crypto.getRandomValues() uses OS-level entropy (hardware RNG, /dev/urandom, etc.)
   * 3. Regulatory compliance often requires provably fair randomness
   * 
   * Browser support: All modern browsers (Chrome 11+, Firefox 21+, Safari 6.1+, Edge 12+)
   * Node.js: Available via 'crypto' module (webcrypto in Node 15+)
   */
  shuffleDeck(deck: Card[]): Card[] {
    const shuffled = [...deck];
    const deckLength = shuffled.length;
    
    //  CSPRNG: Generate cryptographically secure random values

    // Uint32Array gives us 32 bits of entropy per value (4 billion possibilities)
    const randomValues = new Uint32Array(deckLength);
    
    // crypto is available globally in browsers and modern Node.js
    if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
      crypto.getRandomValues(randomValues);
    } else {
      // Fallback for older environments (should never happen in modern browsers)
      console.warn('⚠️ crypto.getRandomValues not available - using fallback');
      for (let i = 0; i < deckLength; i++) {
        randomValues[i] = Math.floor(Math.random() * 0xFFFFFFFF);
      }
    }
    
    // Fisher-Yates shuffle with CSPRNG
    // This produces a uniformly distributed permutation
    for (let i = deckLength - 1; i > 0; i--) {
      // Convert random uint32 to index in range [0, i]
      // Using modulo introduces tiny bias (~1 in 83 million for i=51)
      // This is acceptable for poker (far below detectable threshold)
      const j = randomValues[i] % (i + 1);
      
      // Swap elements
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    
    console.log('🔐 Deck shuffled with cryptographically secure RNG');
    return shuffled;
  }

  // ============================================================================
  // GAME INITIALIZATION
  // ============================================================================

  /**
   * Initialize a new game with players
   * @param players - Array of player objects with id and chips
   * @param smallBlind - Small blind amount
   * @param bigBlind - Big blind amount
   * @param tableId - Optional table identifier
   */
  initializeGame(
    players: { id: string; chips: number; seat?: number }[],
    smallBlind: number,
    bigBlind: number,
    tableId?: string,
    dealerButton: number = 0
  ): GameState {
    console.log('🎰 [gameEngine.initializeGame] Initializing new game');

    // Validate players
    let playersArray = players;
    if (!Array.isArray(players)) {
      if (typeof players === 'object' && players !== null) {
        playersArray = Object.values(players);
      } else {
        throw new Error('initializeGame requires an array of players');
      }
    }

    if (!playersArray || playersArray.length < 2) {
      throw new Error('Cannot initialize game with less than 2 players');
    }

    // Generate and shuffle deck
    const deck = this.generateDeck();
    console.log(`✅ Deck generated: ${deck.length} cards`);

    // Create player objects with proper initialization
    const gamePlayers: Player[] = playersArray.map((p, index) => {
      const chipAmount = typeof p.chips === 'number' && p.chips > 0 ? p.chips : 1000;
      return {
        id: p.id || `player_${index}`,
        chips: chipAmount,
        buyIn: chipAmount, //  Track initial buy-in for loss calculation

        seat: typeof p.seat === 'number' ? p.seat : index, //  PRESERVE existing seat for rotation

        holeCards: [],
        currentBet: 0,
        totalBetThisRound: 0,
        totalBetThisHand: 0, //  Track total bets for entire hand

        folded: false,
        allIn: false,
        hasActedThisRound: false,
        eliminated: false,
        isDealer: false,
        isSmallBlind: false,
        isBigBlind: false,
        isSittingOut: false, //  HEADS-UP FIX: Track sitting out status

      };
    });

    // Create initial game state
    const gameState: GameState = {
      players: gamePlayers,
      communityCards: [],
      deck,
      pot: 0,
      sidePots: [],
      round: 'preflop',
      // Caller-provided so hole cards and blinds post relative to the REAL
      // button. Callers must NOT post blinds again after this returns -
      // double posting deducted a second set of blinds that the pot
      // assignment then silently swallowed.
      dealerButton: Math.max(0, dealerButton) % Math.max(1, gamePlayers.length),
      currentPlayerIndex: 0,
      smallBlind,
      bigBlind,
      status: 'playing',
      timestamp: Date.now(),
      bettingRoundComplete: false,
      handNumber: 1,
    };

    if (tableId) {
      gameState.tableId = tableId;
    }

    // Deal hole cards to all players
    console.log('🃏 Dealing hole cards...');
    this.dealHoleCards(gameState);

    // Post blinds (handles heads-up special case)
    console.log('💰 Posting blinds...');
    this.postBlinds(gameState);

    console.log('✅ Game initialized successfully');
    return gameState;
  }

  // ============================================================================
  // CARD DEALING
  // ============================================================================

  /**
   * Deal 2 hole cards to each non-eliminated player
   * Cards are dealt one at a time, starting left of dealer (standard protocol)
   */
  dealHoleCards(gameState: GameState): void {
    const activePlayers = gameState.players.filter(p => !p.eliminated && !p.isSittingOut);
    
    // Deal 2 rounds of cards (1 card per player per round)
    for (let round = 0; round < 2; round++) {
      for (let i = 0; i < activePlayers.length; i++) {
        // Start dealing from left of dealer
        const dealIndex = (gameState.dealerButton + 1 + i) % activePlayers.length;
        const player = activePlayers[dealIndex];
        
        if (gameState.deck.length > 0) {
          const card = gameState.deck.shift()!; // Deal from top of deck
          player.holeCards.push(card);
        }
      }
    }

    console.log(`✅ Dealt hole cards to ${activePlayers.length} players`);
  }

  /**
   * Alias for dealHoleCards (for compatibility)
   */
  dealCards(gameState: GameState): void {
    this.dealHoleCards(gameState);
  }

  /**
   * Burn one card from the deck (discard face-down)
   * Standard protocol: burn before flop, turn, and river
   */
  private burnCard(gameState: GameState): Card | null {
    if (gameState.deck.length > 0) {
      const burnedCard = gameState.deck.shift()!;
      console.log(`🔥 Burned: ${burnedCard.rank}${burnedCard.suit.charAt(0).toUpperCase()}`);
      return burnedCard;
    }
    return null;
  }

  /**
   * Deal community cards with proper burn card protocol
   * @param gameState - Current game state
   * @param count - Number of cards to deal (3 for flop, 1 for turn/river)
   */
  private dealCommunityCards(gameState: GameState, count: number): void {
    // Always burn before dealing community cards
    this.burnCard(gameState);

    // Deal the community cards
    for (let i = 0; i < count && gameState.deck.length > 0; i++) {
      const card = gameState.deck.shift()!;
      gameState.communityCards.push(card);
      console.log(`🃏 Community card: ${card.rank}${card.suit.charAt(0).toUpperCase()}`);
    }
  }

  // ============================================================================
  // BLIND POSTING
  // ============================================================================

  /**
   * Post small and big blinds
   * IMPORTANT: Handles heads-up (2-player) special case correctly
   */
  postBlinds(gameState: GameState): void {
    const activePlayers = gameState.players.filter(p => !p.eliminated && !p.isSittingOut);
    
    if (activePlayers.length < 2) {
      console.warn('⚠️ Not enough players to post blinds');
      return;
    }

    // Reset position markers
    gameState.players.forEach(p => {
      p.isDealer = false;
      p.isSmallBlind = false;
      p.isBigBlind = false;
    });

    const isHeadsUp = activePlayers.length === 2;

    let smallBlindIdx: number;
    let bigBlindIdx: number;
    let firstToActIdx: number;

    if (isHeadsUp) {
      // HEADS-UP SPECIAL CASE:
      // Dealer = Small Blind, other player = Big Blind
      // Dealer acts first preflop
      smallBlindIdx = gameState.dealerButton;
      bigBlindIdx = (gameState.dealerButton + 1) % gameState.players.length;
      firstToActIdx = smallBlindIdx; // Dealer acts first in heads-up preflop
      
      console.log('🎯 Heads-up mode: Dealer posts SB, acts first preflop');
    } else {
      // STANDARD (3+ players):
      // SB = left of dealer, BB = left of SB
      // UTG (left of BB) acts first
      smallBlindIdx = (gameState.dealerButton + 1) % gameState.players.length;
      bigBlindIdx = (gameState.dealerButton + 2) % gameState.players.length;
      firstToActIdx = (gameState.dealerButton + 3) % gameState.players.length;
    }

    // Skip eliminated/sitting out players for blind positions
    while (gameState.players[smallBlindIdx].eliminated || gameState.players[smallBlindIdx].isSittingOut) {
      smallBlindIdx = (smallBlindIdx + 1) % gameState.players.length;
    }
    while (gameState.players[bigBlindIdx].eliminated || gameState.players[bigBlindIdx].isSittingOut || bigBlindIdx === smallBlindIdx) {
      bigBlindIdx = (bigBlindIdx + 1) % gameState.players.length;
    }
    while (gameState.players[firstToActIdx].eliminated || gameState.players[firstToActIdx].isSittingOut) {
      firstToActIdx = (firstToActIdx + 1) % gameState.players.length;
    }

    const smallBlindPlayer = gameState.players[smallBlindIdx];
    const bigBlindPlayer = gameState.players[bigBlindIdx];
    const dealerPlayer = gameState.players[gameState.dealerButton];

    // Mark positions
    dealerPlayer.isDealer = true;
    smallBlindPlayer.isSmallBlind = true;
    bigBlindPlayer.isBigBlind = true;

    // Store indices in game state
    gameState.smallBlindIndex = smallBlindIdx;
    gameState.bigBlindIndex = bigBlindIdx;

    // Post small blind
    const sbAmount = Math.min(smallBlindPlayer.chips, gameState.smallBlind);
    smallBlindPlayer.chips -= sbAmount;
    smallBlindPlayer.currentBet = sbAmount;
    smallBlindPlayer.totalBetThisRound = sbAmount;
    smallBlindPlayer.totalBetThisHand = (smallBlindPlayer.totalBetThisHand || 0) + sbAmount; //  Track hand total

    smallBlindPlayer.hasActedThisRound = false; // SB hasn't acted yet (posting blind isn't an action)

    // Post big blind
    const bbAmount = Math.min(bigBlindPlayer.chips, gameState.bigBlind);
    bigBlindPlayer.chips -= bbAmount;
    bigBlindPlayer.currentBet = bbAmount;
    bigBlindPlayer.totalBetThisRound = bbAmount;
    bigBlindPlayer.totalBetThisHand = (bigBlindPlayer.totalBetThisHand || 0) + bbAmount; //  Track hand total

    bigBlindPlayer.hasActedThisRound = false; // BB gets option to raise

    // Check if posting blinds put players all-in
    if (smallBlindPlayer.chips === 0) {
      smallBlindPlayer.allIn = true;
      smallBlindPlayer.hasActedThisRound = true; // All-in players have "acted"
    }
    if (bigBlindPlayer.chips === 0) {
      bigBlindPlayer.allIn = true;
      bigBlindPlayer.hasActedThisRound = true; // All-in players have "acted"
    }

    // Add blinds to pot
    gameState.pot = sbAmount + bbAmount;
    gameState.currentBet = bbAmount;

    // Set first player to act
    gameState.currentPlayerIndex = firstToActIdx;
    gameState.turnStartTime = Date.now(); //  TURN TIMER: Start the clock


    // Skip to next active player if first-to-act is folded/all-in/eliminated
    this.advanceToNextActivePlayer(gameState);

    console.log(`💰 Blinds posted: SB $${sbAmount} (seat ${smallBlindIdx}) | BB $${bbAmount} (seat ${bigBlindIdx})`);
    console.log(`💰 Pot: $${gameState.pot} | First to act: seat ${gameState.currentPlayerIndex}`);
  }

  // ============================================================================
  // PLAYER ACTIONS
  // ============================================================================

  /**
   * Process a player action
   * @param gameState - Current game state
   * @param playerId - ID of player taking action
   * @param action - Action type
   * @param amount - Amount for raise (optional)
   */
  playerAction(
    gameState: GameState,
    playerId: string,
    action: 'fold' | 'check' | 'call' | 'raise' | 'all-in',
    amount: number = 0
  ): GameState {
    const playerIndex = gameState.players.findIndex(p => p.id === playerId);
    const player = gameState.players[playerIndex];

    if (!player) {
      console.warn('❌ Player not found:', playerId);
      return gameState;
    }

    // Validate it's this player's turn
    if (gameState.currentPlayerIndex !== playerIndex) {
      console.warn(`❌ Not player ${playerId}'s turn (current: ${gameState.currentPlayerIndex}, player: ${playerIndex})`);
      return gameState;
    }

    // Validate player can act
    if (player.folded || player.eliminated || player.allIn || player.isSittingOut) {
      console.warn(`❌ Player ${playerId} cannot act (folded: ${player.folded}, eliminated: ${player.eliminated}, allIn: ${player.allIn}, sittingOut: ${player.isSittingOut})`);
      return gameState;
    }

    console.log(`🎯 Player ${playerId} action: ${action}${amount > 0 ? ` ($${amount})` : ''}`);

    const maxBet = Math.max(...gameState.players.map(p => p.currentBet));

    switch (action) {
      case 'fold':
        this.handleFold(player);
        break;

      case 'check':
        if (!this.handleCheck(player, maxBet)) {
          return gameState; // Invalid check
        }
        break;

      case 'call':
        this.handleCall(player, maxBet, gameState);
        break;

      case 'raise':
        if (!this.handleRaise(player, amount, maxBet, gameState)) {
          return gameState; // Invalid raise
        }
        break;

      case 'all-in':
        this.handleAllIn(player, maxBet, gameState);
        break;

      default:
        console.warn(`❌ Unknown action: ${action}`);
        return gameState;
    }

    // Mark player as having acted
    player.hasActedThisRound = true;
    
    //  SECURITY: Capture timing for bot detection before updating lastActionTime

    const previousActionTime = player.lastActionTime || 0;
    player.lastActionTime = Date.now();
    
    //  SECURITY: Record action timing for bot detection (non-blocking)

    if (previousActionTime > 0) {
      botDetectionService.recordActionTiming(
        playerId,
        gameState.tableId || 'unknown',
        action,
        player.lastActionTime,
        previousActionTime,
        gameState.round,
        gameState.pot
      ).catch(err => {
        console.warn('⚠️ Bot timing record failed (non-blocking):', err);
      });
    }

    console.log(`💰 Pot: $${gameState.pot.toFixed(2)}`);

    // Check if only one player remains (immediate win)
    const activePlayers = gameState.players.filter(p => !p.folded && !p.eliminated && !p.isSittingOut);
    if (activePlayers.length === 1) {
      console.log(`🏆 Only one player remaining - hand over`);
      gameState.bettingRoundComplete = true;
      return gameState;
    }

    // Move to next player
    this.advanceToNextActivePlayer(gameState);

    // Check if betting round is complete
    if (this.isBettingRoundComplete(gameState)) {
      console.log(`✅ Betting round complete for ${gameState.round}`);
      gameState.bettingRoundComplete = true;
    }

    return gameState;
  }

  /**
   * Handle fold action
   *  NOTE: When player folds, their totalBetThisHand is preserved for loss tracking

   */
  private handleFold(player: Player): void {
    player.folded = true;
    player.lastAction = 'fold';
    //  DO NOT reset totalBetThisHand - we need it for loss tracking

    console.log(`🃏 Player folded (contributed $${player.totalBetThisHand || 0} to pot before folding)`);
  }

  /**
   * Handle check action
   * @returns true if check is valid, false otherwise
   */
  private handleCheck(player: Player, maxBet: number): boolean {
    if (maxBet > player.currentBet) {
      console.warn(`❌ Cannot check - must call $${maxBet - player.currentBet}`);
      return false;
    }
    player.lastAction = 'check';
    console.log(`✓ Player checked`);
    return true;
  }

  /**
   * Handle call action
   */
  private handleCall(player: Player, maxBet: number, gameState: GameState): void {
    const callAmount = Math.min(maxBet - player.currentBet, player.chips);
    
    player.chips -= callAmount;
    player.currentBet += callAmount;
    player.totalBetThisRound += callAmount;
    player.totalBetThisHand = (player.totalBetThisHand || 0) + callAmount; //  Track hand total

    gameState.pot += callAmount;
    player.lastAction = 'call';
    player.lastActionAmount = callAmount;

    // Check if call put player all-in
    if (player.chips === 0) {
      player.allIn = true;
      player.lastAction = 'all-in';
      console.log(`📞 Player called $${callAmount} (ALL-IN)`);
    } else {
      console.log(`📞 Player called $${callAmount}`);
    }
  }

  /**
   * Handle raise action
   * @returns true if raise is valid, false otherwise
   */
  private handleRaise(
    player: Player,
    raiseAmount: number,
    maxBet: number,
    gameState: GameState
  ): boolean {
    // Calculate minimum raise (must be at least the size of the last raise or big blind)
    const lastRaise = this.getLastRaiseAmount(gameState);
    const minRaise = lastRaise > 0 ? lastRaise : gameState.bigBlind;

    // Total amount player needs to put in
    const callAmount = maxBet - player.currentBet;
    const totalBet = callAmount + raiseAmount;

    // Validate raise amount (unless going all-in)
    if (raiseAmount < minRaise && totalBet < player.chips) {
      console.warn(`❌ Raise must be at least $${minRaise} (got $${raiseAmount})`);
      return false;
    }

    // Check if player has enough chips
    if (totalBet > player.chips) {
      console.warn(`❌ Not enough chips for raise (need $${totalBet}, have $${player.chips})`);
      return false;
    }

    // Execute raise
    player.chips -= totalBet;
    player.currentBet += totalBet;
    player.totalBetThisRound += totalBet;
    player.totalBetThisHand = (player.totalBetThisHand || 0) + totalBet; //  Track hand total

    gameState.pot += totalBet;
    player.lastAction = 'raise';
    player.lastActionAmount = raiseAmount;

    // Reset hasActedThisRound for other players (they must respond to raise)
    gameState.players.forEach(p => {
      if (p.id !== player.id && !p.folded && !p.allIn && !p.eliminated && !p.isSittingOut) {
        p.hasActedThisRound = false;
      }
    });

    // Check if raise put player all-in
    if (player.chips === 0) {
      player.allIn = true;
      console.log(`📈 Player raised $${raiseAmount} (ALL-IN, total bet: $${player.currentBet})`);
    } else {
      console.log(`📈 Player raised $${raiseAmount} (total bet: $${player.currentBet})`);
    }

    return true;
  }

  /**
   * Handle all-in action
   */
  private handleAllIn(player: Player, maxBet: number, gameState: GameState): void {
    const allInAmount = player.chips;
    const previousBet = player.currentBet;
    
    // Calculate what the new total bet will be
    const newTotalBet = previousBet + allInAmount;
    
    // Add chips to pot and update player state
    player.currentBet = newTotalBet;
    player.totalBetThisRound += allInAmount;
    player.totalBetThisHand = (player.totalBetThisHand || 0) + allInAmount; //  Track hand total

    gameState.pot += allInAmount;
    player.chips = 0;
    player.allIn = true;
    player.lastAction = 'all-in';
    player.lastActionAmount = allInAmount;

    console.log(`🚀 Player went ALL-IN: $${allInAmount} (total bet: $${newTotalBet})`);

    // Determine if this all-in is effectively a raise
    if (newTotalBet > maxBet) {
      const raiseAmount = newTotalBet - maxBet;
      const minRaise = this.getLastRaiseAmount(gameState) || gameState.bigBlind;
      
      //  FIX Issue #5: Only reopen action if raise meets minimum

      // Removed broken condition (allInAmount === player.lastActionAmount) which was always true
      if (raiseAmount >= minRaise) {
        console.log(`   ↳ All-in is a RAISE of $${raiseAmount} - other players must respond`);
        
        // Reset hasActedThisRound for other players who can still act
        gameState.players.forEach(p => {
          if (p.id !== player.id && !p.folded && !p.allIn && !p.eliminated && !p.isSittingOut) {
            p.hasActedThisRound = false;
          }
        });
      } else {
        console.log(`   ↳ All-in is an UNDER-RAISE ($${raiseAmount} < min $${minRaise}) - no reopen`);
      }
    } else {
      console.log(`   ↳ All-in is a CALL (total ${newTotalBet} <= max ${maxBet})`);
    }
  }

  /**
   * Get the last raise amount for minimum raise calculation
   */
  private getLastRaiseAmount(gameState: GameState): number {
    const raisingPlayers = gameState.players.filter(
      p => p.lastAction === 'raise' && p.lastActionAmount
    );
    
    if (raisingPlayers.length === 0) return 0;
    
    return Math.max(...raisingPlayers.map(p => p.lastActionAmount || 0));
  }

  // ============================================================================
  // TURN MANAGEMENT
  // ============================================================================

  /**
   * Advance to the next player who can act
   */
  private advanceToNextActivePlayer(gameState: GameState): void {
    const startIndex = gameState.currentPlayerIndex;
    let nextIndex = (startIndex + 1) % gameState.players.length;
    let iterations = 0;

    while (iterations < gameState.players.length) {
      const player = gameState.players[nextIndex];
      
      // Skip folded, all-in, eliminated, and sitting out players
      if (!player.folded && !player.allIn && !player.eliminated && !player.isSittingOut) {
        gameState.currentPlayerIndex = nextIndex;
        gameState.turnStartTime = Date.now(); //  TURN TIMER: Reset clock for new player

        return;
      }
      
      nextIndex = (nextIndex + 1) % gameState.players.length;
      iterations++;
    }

    // If we've looped through all players, stay at current index
    console.log(`⚠️ No active players to advance to`);
  }

  /**
   * Get the first active player after the dealer for post-flop action
   */
  private getFirstPostFlopPlayer(gameState: GameState): number {
    const activePlayers = gameState.players.filter(p => !p.eliminated && !p.isSittingOut);
    const isHeadsUp = activePlayers.length === 2;

    let startIdx: number;

    if (isHeadsUp) {
      // In heads-up post-flop, big blind acts first (non-dealer)
      startIdx = gameState.bigBlindIndex ?? (gameState.dealerButton + 1) % gameState.players.length;
    } else {
      // Standard: first active player left of dealer
      startIdx = (gameState.dealerButton + 1) % gameState.players.length;
    }

    // Find first non-folded, non-eliminated, non-all-in, non-sitting-out player
    let idx = startIdx;
    let iterations = 0;

    while (iterations < gameState.players.length) {
      const player = gameState.players[idx];
      if (!player.folded && !player.eliminated && !player.allIn && !player.isSittingOut) {
        return idx;
      }
      idx = (idx + 1) % gameState.players.length;
      iterations++;
    }

    return startIdx;
  }

  // ============================================================================
  // BETTING ROUND MANAGEMENT
  // ============================================================================

  /**
   * Check if the current betting round is complete
   */
  isBettingRoundComplete(gameState: GameState): boolean {
    const activePlayers = gameState.players.filter(
      p => !p.folded && !p.eliminated && !p.isSittingOut
    );

    console.log(`🔍 Checking betting complete (${gameState.round}):`);
    console.log(`   Active players: ${activePlayers.length}`);

    // Only 1 player left = betting complete (hand over)
    if (activePlayers.length <= 1) {
      console.log('   ✅ Only 1 player remaining - hand over');
      return true;
    }

    // Get players who can still act (not all-in)
    const playersWhoCanAct = activePlayers.filter(p => !p.allIn && p.chips > 0);
    
    // If no one can act (everyone all-in or folded), betting is complete
    if (playersWhoCanAct.length === 0) {
      console.log('   ✅ No players can act (all are all-in)');
      return true;
    }

    // If only one player can act and they're not facing a bet, complete
    if (playersWhoCanAct.length === 1) {
      const lastPlayer = playersWhoCanAct[0];
      const maxBet = Math.max(...activePlayers.map(p => p.currentBet));
      
      if (lastPlayer.currentBet >= maxBet && lastPlayer.hasActedThisRound) {
        console.log('   ✅ Only one player can act and they have matched/acted');
        return true;
      }
    }

    // Get max bet among active players
    const maxBet = Math.max(...activePlayers.map(p => p.currentBet));
    console.log(`   Max bet: $${maxBet}`);

    // Check each player who can act
    for (const player of playersWhoCanAct) {
      // Player must have acted this round
      if (!player.hasActedThisRound) {
        console.log(`   Player ${player.id.substring(0, 8)}: has NOT acted ❌`);
        return false;
      }

      // Player must have matched the bet
      if (player.currentBet < maxBet) {
        console.log(`   Player ${player.id.substring(0, 8)}: bet $${player.currentBet} < max $${maxBet} ❌`);
        return false;
      }

      console.log(`   Player ${player.id.substring(0, 8)}: acted, bet matched ✓`);
    }

    // SPECIAL CASE: Preflop big blind option
    if (gameState.round === 'preflop') {
      const bbIdx = gameState.bigBlindIndex ?? (gameState.dealerButton + 2) % gameState.players.length;
      const bbPlayer = gameState.players[bbIdx];

      if (
        bbPlayer &&
        !bbPlayer.folded &&
        !bbPlayer.allIn &&
        !bbPlayer.eliminated &&
        !bbPlayer.isSittingOut &&
        !bbPlayer.hasActedThisRound &&
        maxBet === gameState.bigBlind
      ) {
        console.log('   ⏳ Waiting for big blind option');
        return false;
      }
    }

    console.log('   ✅ BETTING ROUND COMPLETE');
    return true;
  }

  /**
   * Check if all remaining players are all-in (no more betting possible)
   */
  isAllInShowdown(gameState: GameState): boolean {
    const activePlayers = gameState.players.filter(p => !p.folded && !p.eliminated && !p.isSittingOut);
    
    if (activePlayers.length <= 1) {
      return false; // Not a showdown if only 1 player
    }

    const playersWhoCanAct = activePlayers.filter(p => !p.allIn && p.chips > 0);
    
    // All-in showdown if 0 or 1 player can still act
    return playersWhoCanAct.length <= 1;
  }

  /**
   * Progress to the next round
   *  CRITICAL FIX: Only reset totalBetThisRound, NOT totalBetThisHand

   */
  nextRound(gameState: GameState): GameState {
    //  FIXED: Reset per-ROUND tracking, but NOT per-HAND tracking

    gameState.players.forEach(p => {
      p.currentBet = 0;
      p.totalBetThisRound = 0;
      //  DO NOT RESET totalBetThisHand - it accumulates across all betting rounds!

      p.hasActedThisRound = false;
      p.lastAction = undefined;
      p.lastActionAmount = undefined;
    });

    gameState.currentBet = 0;
    gameState.bettingRoundComplete = false;

    // Check for immediate win (only 1 player remaining)
    const activePlayers = gameState.players.filter(p => !p.folded && !p.eliminated && !p.isSittingOut);

    if (activePlayers.length <= 1) {
      console.log('🎉 Only 1 player remaining - hand over');
      gameState.round = 'showdown';
      this.determineWinners(gameState);
      return gameState;
    }

    // Check if all remaining players are all-in (no more betting possible)
    const playersWhoCanAct = activePlayers.filter(p => !p.allIn && p.chips > 0);

    if (playersWhoCanAct.length <= 1 && activePlayers.length > 1) {
      console.log('🎰 All players all-in - dealing remaining cards');
      this.dealRemainingCards(gameState);
      gameState.round = 'showdown';
      this.determineWinners(gameState);
      return gameState;
    }

    // Progress to next street
    switch (gameState.round) {
      case 'preflop':
        this.dealCommunityCards(gameState, 3); // Flop: burn 1, deal 3
        gameState.round = 'flop';
        console.log(`📊 FLOP: ${gameState.communityCards.length} cards`);
        break;

      case 'flop':
        this.dealCommunityCards(gameState, 1); // Turn: burn 1, deal 1
        gameState.round = 'turn';
        console.log(`📊 TURN: ${gameState.communityCards.length} cards`);
        break;

      case 'turn':
        this.dealCommunityCards(gameState, 1); // River: burn 1, deal 1
        gameState.round = 'river';
        console.log(`📊 RIVER: ${gameState.communityCards.length} cards`);
        break;

      case 'river':
        gameState.round = 'showdown';
        this.determineWinners(gameState);
        return gameState;

      default:
        console.warn(`⚠️ Unknown round: ${gameState.round}`);
        break;
    }

    // Set first player to act for post-flop rounds
    gameState.currentPlayerIndex = this.getFirstPostFlopPlayer(gameState);
    gameState.turnStartTime = Date.now(); //  TURN TIMER: Reset clock for new round


    console.log(`🎯 Action to player at seat ${gameState.currentPlayerIndex}`);

    return gameState;
  }

  /**
   * Deal remaining community cards (for all-in scenarios)
   */
  private dealRemainingCards(gameState: GameState): void {
    const cardsNeeded = 5 - gameState.communityCards.length;
    
    for (let i = 0; i < cardsNeeded && gameState.deck.length > 0; i++) {
      this.dealCommunityCards(gameState, 1);
    }
    
    console.log(`📊 Board complete: ${gameState.communityCards.length} cards`);
  }

  /**
   * Check and progress round if betting is complete
   */
  checkAndProgressRound(gameState: GameState): void {
    if (!this.isBettingRoundComplete(gameState)) {
      return;
    }
    this.nextRound(gameState);
  }

  // ============================================================================
  // HAND EVALUATION
  // ============================================================================

  /**
   * Evaluate a player's best 5-card hand from 7 cards
   */
  evaluateHand(holeCards: Card[], communityCards: Card[]): Hand | null {
    const allCards = [...holeCards, ...communityCards];
    
    if (allCards.length < 5) {
      return null;
    }

    // Generate all 5-card combinations
    const combinations = this.getCombinations(allCards, 5);
    
    let bestHand: Hand | null = null;

    for (const combo of combinations) {
      const hand = this.rankHand(combo);
      if (!bestHand || this.compareHands(hand, bestHand) > 0) {
        bestHand = hand;
      }
    }

    return bestHand;
  }

  /**
   * Get all combinations of size k from an array
   */
  getCombinations(cards: Card[], size: number): Card[][] {
    if (size === 0) return [[]];
    if (cards.length === 0) return [];

    const [first, ...rest] = cards;
    const withoutFirst = this.getCombinations(rest, size);
    const withFirst = this.getCombinations(rest, size - 1).map(combo => [first, ...combo]);

    return [...withFirst, ...withoutFirst];
  }

  /**
   * Rank a 5-card hand
   */
  rankHand(cards: Card[]): Hand {
    // Sort cards by rank value (high to low)
    const sortedCards = [...cards].sort(
      (a, b) => RANK_VALUES[b.rank] - RANK_VALUES[a.rank]
    );

    const isFlush = this.isFlush(sortedCards);
    const isStraight = this.isStraight(sortedCards);
    const rankCounts = this.getRankCounts(sortedCards);
    const counts = Object.values(rankCounts).sort((a, b) => b - a);

    let rank: HandRankType;
    let tiebreakers: number[];

    // Check for each hand type from best to worst
    if (isFlush && isStraight) {
      const highCard = this.getStraightHighCard(sortedCards);
      if (highCard === 14) {
        rank = 'Royal Flush';
      } else {
        rank = 'Straight Flush';
      }
      tiebreakers = [highCard];
    } else if (counts[0] === 4) {
      rank = 'Four of a Kind';
      tiebreakers = this.getGroupedTiebreakers(rankCounts, [4, 1]);
    } else if (counts[0] === 3 && counts[1] === 2) {
      rank = 'Full House';
      tiebreakers = this.getGroupedTiebreakers(rankCounts, [3, 2]);
    } else if (isFlush) {
      rank = 'Flush';
      tiebreakers = sortedCards.map(c => RANK_VALUES[c.rank]);
    } else if (isStraight) {
      rank = 'Straight';
      tiebreakers = [this.getStraightHighCard(sortedCards)];
    } else if (counts[0] === 3) {
      rank = 'Three of a Kind';
      tiebreakers = this.getGroupedTiebreakers(rankCounts, [3, 1, 1]);
    } else if (counts[0] === 2 && counts[1] === 2) {
      rank = 'Two Pair';
      tiebreakers = this.getGroupedTiebreakers(rankCounts, [2, 2, 1]);
    } else if (counts[0] === 2) {
      rank = 'Pair';
      tiebreakers = this.getGroupedTiebreakers(rankCounts, [2, 1, 1, 1]);
    } else {
      rank = 'High Card';
      tiebreakers = sortedCards.map(c => RANK_VALUES[c.rank]);
    }

    return {
      cards: sortedCards,
      rank,
      highCards: sortedCards,
      tiebreakers,
    };
  }

  /**
   * Check if cards form a flush (all same suit)
   */
  private isFlush(cards: Card[]): boolean {
    return cards.every(c => c.suit === cards[0].suit);
  }

  /**
   * Check if cards form a straight (sequential ranks)
   */
  private isStraight(cards: Card[]): boolean {
    const values = cards.map(c => RANK_VALUES[c.rank]).sort((a, b) => b - a);
    
    // Check for regular straight
    let isRegularStraight = true;
    for (let i = 0; i < values.length - 1; i++) {
      if (values[i] - values[i + 1] !== 1) {
        isRegularStraight = false;
        break;
      }
    }
    
    if (isRegularStraight) return true;
    
    // Check for wheel (A-2-3-4-5)
    const wheel = [14, 5, 4, 3, 2];
    if (values.length === 5 && values.every((v, i) => v === wheel[i])) {
      return true;
    }
    
    return false;
  }

  /**
   * Get the high card of a straight
   */
  private getStraightHighCard(cards: Card[]): number {
    const values = cards.map(c => RANK_VALUES[c.rank]).sort((a, b) => b - a);
    
    // Check for wheel (A-2-3-4-5) - high card is 5
    const wheel = [14, 5, 4, 3, 2];
    if (values.length === 5 && values.every((v, i) => v === wheel[i])) {
      return 5;
    }
    
    return values[0];
  }

  /**
   * Count occurrences of each rank
   */
  private getRankCounts(cards: Card[]): { [key: string]: number } {
    const counts: { [key: string]: number } = {};
    for (const card of cards) {
      counts[card.rank] = (counts[card.rank] || 0) + 1;
    }
    return counts;
  }

  /**
   * Get tiebreakers for grouped hands (pairs, trips, etc.)
   */
  private getGroupedTiebreakers(
    rankCounts: { [key: string]: number },
    expectedCounts: number[]
  ): number[] {
    const groups: { rank: string; count: number; value: number }[] = [];

    for (const [rank, count] of Object.entries(rankCounts)) {
      groups.push({
        rank,
        count,
        value: RANK_VALUES[rank as CardRank],
      });
    }

    // Sort by count (descending) then by value (descending)
    groups.sort((a, b) => {
      if (a.count !== b.count) return b.count - a.count;
      return b.value - a.value;
    });

    return groups.map(g => g.value);
  }

  /**
   * Compare two hands
   * @returns positive if hand1 wins, negative if hand2 wins, 0 if tie
   */
  compareHands(hand1: Hand, hand2: Hand): number {
    const rank1 = HAND_RANK_VALUES[hand1.rank];
    const rank2 = HAND_RANK_VALUES[hand2.rank];

    if (rank1 !== rank2) {
      return rank1 - rank2;
    }

    // Same rank - compare tiebreakers
    for (let i = 0; i < Math.min(hand1.tiebreakers.length, hand2.tiebreakers.length); i++) {
      if (hand1.tiebreakers[i] !== hand2.tiebreakers[i]) {
        return hand1.tiebreakers[i] - hand2.tiebreakers[i];
      }
    }

    return 0; // Tie
  }

  // ============================================================================
  // WINNER DETERMINATION
  // ============================================================================

  /**
   * Determine the winner(s) of the hand
   */
  determineWinners(gameState: GameState): void {
    const activePlayers = gameState.players.filter(p => !p.folded && !p.eliminated && !p.isSittingOut);

    // Single player remaining (all others folded)
    if (activePlayers.length === 1) {
      const winner = activePlayers[0];
      const potWon = gameState.pot;
      winner.chips += potWon;

      console.log(`🎉 Player ${winner.id} wins $${potWon.toFixed(2)} (all others folded)`);

      gameState.handResult = {
        winnerId: winner.id,
        winnerName: '',
        winningHand: null,
        loserIds: gameState.players.filter(p => p.folded).map(p => p.id),
        potWon,
        mainPot: potWon,
        sidePots: [],
        timestamp: Date.now(),
        round: gameState.round,
      };

      gameState.winners = [{
        playerId: winner.id,
        amount: potWon,
        hand: undefined,
        handRank: null,
      }];

      gameState.status = 'ended';
      gameState.round = 'ended';
      return;
    }

    // ── SIDE POTS (all-in for less) ──
    // Build pot layers from each player's total contribution this hand.
    // Each layer pays the best hand among the non-folded players who
    // covered that layer; a layer only one player funded is their own
    // uncalled excess and comes straight back to them. Folded players'
    // money lives in whatever layers they reached and goes to those
    // layers' winners. Without this, a covering player who lost an
    // all-in was donating their uncalled chips to the winner.
    const contributions = gameState.players.map(p => ({
      player: p,
      total: p.totalBetThisHand || 0,
    }));
    const potTotal = contributions.reduce((s, c) => s + c.total, 0) || gameState.pot;

    const handByPlayer = new Map<string, Hand | null>();
    activePlayers.forEach(p =>
      handByPlayer.set(p.id, this.evaluateHand(p.holeCards, gameState.communityCards))
    );

    // Layer boundaries: every active player's contribution level, plus the
    // overall max so uncalled excess forms its own (single-funder) layer.
    const levels = [...new Set([
      ...activePlayers.map(p => p.totalBetThisHand || 0),
      Math.max(0, ...contributions.map(c => c.total)),
    ])].filter(l => l > 0).sort((a, b) => a - b);

    const totalWon = new Map<string, number>();
    let prevLevel = 0;

    for (const level of levels) {
      const layerAmt = contributions.reduce(
        (s, c) => s + Math.max(0, Math.min(c.total, level) - prevLevel), 0
      );
      const eligible = activePlayers.filter(p => (p.totalBetThisHand || 0) >= level);
      prevLevel = level;
      if (layerAmt <= 0) continue;

      let layerWinners: typeof activePlayers;
      if (eligible.length === 0) {
        // Should not occur; conserve chips by returning to the top funder.
        const top = [...contributions].sort((a, b) => b.total - a.total)[0];
        layerWinners = [top.player];
      } else if (eligible.length === 1) {
        layerWinners = [eligible[0]]; // uncalled excess refund
      } else {
        let best: Hand | null = null;
        for (const p of eligible) {
          const h = handByPlayer.get(p.id) || null;
          if (h && (!best || this.compareHands(h, best) > 0)) best = h;
        }
        layerWinners = eligible.filter(p => {
          const h = handByPlayer.get(p.id);
          return !!(h && best && this.compareHands(h, best) === 0);
        });
        if (layerWinners.length === 0) layerWinners = eligible;
      }

      const share = Math.floor(layerAmt / layerWinners.length);
      const rem = layerAmt - share * layerWinners.length;
      layerWinners.forEach((p, idx) => {
        const amount = share + (idx === 0 ? rem : 0);
        p.chips += amount;
        totalWon.set(p.id, (totalWon.get(p.id) || 0) + amount);
      });
    }

    const winnersArr = [...totalWon.entries()]
      .map(([playerId, amount]) => ({
        playerId,
        amount,
        hand: handByPlayer.get(playerId)?.rank,
        handRank: handByPlayer.get(playerId) || null,
      }))
      .filter(w => w.amount > 0)
      .sort((a, b) => b.amount - a.amount);

    const headline = winnersArr[0];
    winnersArr.forEach(w =>
      console.log(`🏆 Player ${w.playerId} collects $${w.amount}${w.hand ? ` with ${w.hand}` : ' (refund/side pot)'}`)
    );

    // Record result
    gameState.handResult = {
      winnerId: headline?.playerId || activePlayers[0].id,
      winnerName: '',
      winningHand: headline ? (handByPlayer.get(headline.playerId) || null) : null,
      loserIds: activePlayers
        .filter(p => !totalWon.has(p.id))
        .map(p => p.id),
      potWon: headline?.amount || 0,
      mainPot: potTotal,
      sidePots: gameState.sidePots,
      timestamp: Date.now(),
      round: gameState.round,
    };

    gameState.winners = winnersArr;

    gameState.status = 'ended';
    gameState.round = 'ended';

    console.log(`🎉 Hand complete. Winner(s): ${winnersArr.map(w => w.playerId).join(', ')}`);
  }

  // ============================================================================
  // UTILITY METHODS
  // ============================================================================

  /**
   * Get community cards visible for current round
   */
  getCommunityCardsForRound(gameState: GameState): Card[] {
    switch (gameState.round) {
      case 'preflop':
        return [];
      case 'flop':
        return gameState.communityCards.slice(0, 3);
      case 'turn':
        return gameState.communityCards.slice(0, 4);
      case 'river':
      case 'showdown':
      case 'ended':
        return gameState.communityCards;
      default:
        return [];
    }
  }

  /**
   *  HEADS-UP FIX: Prepare for next hand with proper bust detection

   * Marks busted players as "sitting out" instead of "eliminated" for cash games
   */
  prepareNextHand(gameState: GameState): void {
    console.log('🔄 Preparing for next hand...');

    //  HEADS-UP FIX: Mark busted players as sitting out (not eliminated)

    // In cash games, busted players can buy back in
    gameState.players.forEach(p => {
      if (p.chips === 0 && !p.allIn && !p.eliminated) {
        //  Set sitting out instead of eliminated for cash games

        p.isSittingOut = true;
        p.eliminated = false; // Don't permanently eliminate in cash games
        console.log(`💺 Player ${p.id} is sitting out (busted - needs buy-in)`);
      }
    });

    // Check if game can continue
    const remainingPlayers = gameState.players.filter(p => !p.eliminated && !p.isSittingOut);
    if (remainingPlayers.length < 2) {
      console.log('⏸️ Waiting for more players - game paused');
      gameState.status = 'waiting';
    }
  }

  /**
   *  HEADS-UP FIX: Check if a busted player can rejoin after buy-in

   */
  canPlayerRejoin(gameState: GameState, playerId: string): boolean {
    const player = gameState.players.find(p => p.id === playerId);
    if (!player) return false;
    
    // Player can rejoin if they're sitting out and have chips now
    return player.isSittingOut === true && player.chips > 0;
  }

  /**
   *  HEADS-UP FIX: Rejoin a player who bought back in

   */
  rejoinPlayer(gameState: GameState, playerId: string): boolean {
    const player = gameState.players.find(p => p.id === playerId);
    if (!player) {
      console.warn(`❌ Player ${playerId} not found`);
      return false;
    }
    
    if (!player.isSittingOut) {
      console.log(`ℹ️ Player ${playerId.substring(0, 8)} is not sitting out`);
      return false;
    }
    
    if (player.chips <= 0) {
      console.warn(`❌ Player ${playerId.substring(0, 8)} has no chips - cannot rejoin`);
      return false;
    }
    
    //  Rejoin the player

    player.isSittingOut = false;
    player.eliminated = false;
    player.folded = false;
    player.allIn = false;
    
    console.log(`✅ Player ${playerId.substring(0, 8)} rejoined with ${player.chips} chips`);
    
    // Check if game can now continue
    const playablePlayers = gameState.players.filter(p => !p.eliminated && !p.isSittingOut && p.chips > 0);
    if (playablePlayers.length >= 2 && gameState.status === 'waiting') {
      console.log('🎮 Enough players - game can resume');
      gameState.status = 'ready'; // Signal that game can start new hand
    }
    
    return true;
  }

  /**
   *  HEADS-UP FIX: Check if game should auto-start after player rejoins

   */
  shouldAutoStartNextHand(gameState: GameState): boolean {
    // Game must be in waiting or ready state
    if (gameState.status !== 'waiting' && gameState.status !== 'ready') {
      return false;
    }
    
    // Need at least 2 playable players
    const playablePlayers = gameState.players.filter(
      p => !p.eliminated && !p.isSittingOut && p.chips > 0
    );
    
    return playablePlayers.length >= 2;
  }

  /**
   * Start a new hand
   *  CRITICAL FIX: Reset totalBetThisHand to 0 at start of new hand

   *  HEADS-UP FIX: Properly handles players rejoining

   *  FIX #5: Clear ALL transitional flags

   */
  startNextHand(gameState: GameState): void {
    console.log('🎰 Starting next hand...');

    // Reset pot and cards
    gameState.pot = 0;
    gameState.sidePots = [];
    gameState.communityCards = [];
    gameState.deck = this.generateDeck();

    //  FIX #5: Remove players marked as leaving

    const leavingPlayers = gameState.players.filter(p => (p as any).leavingTable);
    if (leavingPlayers.length > 0) {
      console.log(`🚪 [GameEngine] Removing ${leavingPlayers.length} players who left during last hand`);
      gameState.players = gameState.players.filter(p => !(p as any).leavingTable);
    }

    // Get playable players (not eliminated, not sitting out, has chips)
    const playablePlayers = gameState.players.filter(
      p => !p.eliminated && !p.isSittingOut && p.chips > 0
    );

    if (playablePlayers.length < 2) {
      console.warn('⚠️ Not enough players to start hand');
      gameState.status = 'waiting';
      return;
    }

    // Rotate dealer button (skip eliminated/sitting out players)
    let nextDealer = (gameState.dealerButton + 1) % gameState.players.length;
    let iterations = 0;
    while ((gameState.players[nextDealer].eliminated || gameState.players[nextDealer].isSittingOut) && iterations < gameState.players.length) {
      nextDealer = (nextDealer + 1) % gameState.players.length;
      iterations++;
    }
    gameState.dealerButton = nextDealer;

    // Reset player state for new hand - only for active players
    gameState.players.forEach(p => {
      // Only reset players who are:
      // - Not eliminated
      // - Not sitting out (check BOTH field names for consistency)
      // - Don't need rebuy (haven't rebuyed yet)
      // - Have chips to play with
      const isSittingOutAny = p.isSittingOut || (p as any).sittingOut;
      const canPlayThisHand = !p.eliminated && !isSittingOutAny && !(p as any).needsRebuy && p.chips > 0;
      
      if (canPlayThisHand) {
        //  Update buyIn to current chips at start of new hand

        // This tracks what they started THIS hand with (for loss calculation)
        p.buyIn = p.chips;
        p.holeCards = [];
        p.currentBet = 0;
        p.totalBetThisRound = 0;
        p.totalBetThisHand = 0; //  RESET at start of NEW HAND

        p.folded = false;
        p.allIn = false;
        p.hasActedThisRound = false;
        p.lastAction = undefined;
        p.lastActionAmount = undefined;
        p.isDealer = false;
        p.isSmallBlind = false;
        p.isBigBlind = false;
        
        // Clear waitingForNextHand - they're now in the hand
        (p as any).waitingForNextHand = false;
        // NOTE: Do NOT clear needsRebuy here - only rebuy action clears that
        // NOTE: Do NOT clear leavingTable - those players are removed above
      }
    });

    // Reset game state
    gameState.round = 'preflop';
    gameState.status = 'playing';
    gameState.handResult = undefined;
    gameState.winners = undefined;
    gameState.bettingRoundComplete = false;
    gameState.lastProgressTime = undefined;
    gameState.handNumber = (gameState.handNumber || 1) + 1;

    // Deal and post blinds
    this.dealHoleCards(gameState);
    this.postBlinds(gameState);

    console.log(`✅ Hand #${gameState.handNumber} started. Dealer: seat ${gameState.dealerButton}`);
  }

  /**
   * End the game
   */
  endGame(gameState: GameState): void {
    gameState.status = 'ended';
    console.log('🏁 Game ended');
  }

  /**
   *  HEADS-UP FIX: Get game status for UI

   */
  getGameStatus(gameState: GameState): {
    canStart: boolean;
    playablePlayers: number;
    sittingOutPlayers: string[];
    waitingMessage?: string;
  } {
    const playable = gameState.players.filter(
      p => !p.eliminated && !p.isSittingOut && p.chips > 0
    );
    
    const sittingOut = gameState.players
      .filter(p => p.isSittingOut)
      .map(p => p.id);
    
    let waitingMessage: string | undefined;
    
    if (playable.length < 2) {
      if (sittingOut.length > 0) {
        waitingMessage = 'Waiting for player to buy back in...';
      } else {
        waitingMessage = 'Waiting for more players...';
      }
    }
    
    return {
      canStart: playable.length >= 2,
      playablePlayers: playable.length,
      sittingOutPlayers: sittingOut,
      waitingMessage,
    };
  }
}

// ============================================================================
// EXPORT
// ============================================================================

export const gameEngine = new GameEngine();
export default gameEngine;